import React, { useState, useEffect, useCallback } from 'react';
import {
	AppBar,
	Toolbar,
	Typography,
	Button,
	Box,
	IconButton,
	Menu,
	MenuItem,
	Avatar,
	Divider,
	Badge,
	Popover,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	CircularProgress,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	GlobalStyles,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
	Person as PersonIcon,
	Logout as LogoutIcon,
	ChatBubbleOutline as ChatIcon,
	SupportAgent as SupportAgentIcon,
	NotificationsNone as NotificationsBellIcon,
	Notifications as NotificationsActiveIcon,
	Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { settingsService, chatService, userService, notificationService } from '../services/api';
import { useSocket } from '../context/SocketContext';
import FloatingChatWindow from './FloatingChatWindow';
import ProfileModal from './ProfileModal';

const IMG_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8888/api').replace('/api', '') + '/public';
const getCoverUrl = (coverImage) => {
	if (!coverImage) return undefined;
	const p = typeof coverImage === 'object' ? coverImage.path : coverImage;
	return p ? `${ IMG_BASE }${ p }` : undefined;
};
const formatVND = (v) => {
	if (v == null || isNaN(v)) return '';
	return new Intl.NumberFormat('vi-VN').format(Math.round(v)) + ' ₫';
};

const LOGO_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8888/api').replace('/api', '') + '/public';
const getAvatarUrl = (avatar) => {
	if (!avatar) return undefined;
	const p = typeof avatar === 'object' ? avatar.path : avatar;
	return p ? `${ LOGO_BASE }${ p }` : undefined;
};

const Navbar = () => {
	const { user, isAuthenticated, logout } = useAuth();
	const { socket } = useSocket();
	const navigate = useNavigate();
	const location = useLocation();
	const isHome = location.pathname === '/';
	const [anchorEl, setAnchorEl] = useState(null);
	const [profileOpen, setProfileOpen] = useState(false);
	const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
	const [siteSettings, setSiteSettings] = useState(null);
	const [scrolled, setScrolled] = useState(false);
	const [mobileOpen, setMobileOpen] = useState(false);

	// Close mobile overlay on route change
	useEffect(() => {
		setMobileOpen(false);
	}, [location.pathname]);

	// Chat popover state
	const [chatAnchorEl, setChatAnchorEl] = useState(null);
	const [activeAdmins, setActiveAdmins] = useState([]);
	const [loadingAdmins, setLoadingAdmins] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const [unreadByAdmin, setUnreadByAdmin] = useState({}); // { [adminId]: count }
	const [openingChat, setOpeningChat] = useState(false);
	const [activeConversation, setActiveConversation] = useState(null);

	// Notification bell state
	const [notifAnchorEl, setNotifAnchorEl] = useState(null);
	const [notifications, setNotifications] = useState([]);
	const [notifCount, setNotifCount] = useState(0);
	const userId = user?.id || user?._id;

	const isAdminRole = user?.role === 'admin';
	// Chỉ hiện chat icon cho user thường (admin đã có chat riêng trong AdminLayout)
	const showChat = isAuthenticated && !isAdminRole;

	useEffect(() => {
		if (!isAuthenticated || isAdminRole) return;
		notificationService.getAll({ limit: 20 })
			.then((res) => {
				if (res?.success) {
					setNotifications(res.data || []);
					setNotifCount(res.unreadCount || 0);
				}
			})
			.catch(() => { });
	}, [isAuthenticated, isAdminRole]);

	useEffect(() => {
		if (!socket || !isAuthenticated || isAdminRole) return;
		const handler = (notif) => {
			setNotifications((prev) => [notif, ...prev].slice(0, 20));
			setNotifCount((c) => c + 1);
		};
		socket.on('notification:new', handler);
		return () => socket.off('notification:new', handler);
	}, [socket, isAuthenticated, isAdminRole]);

	const handleNotifOpen = (e) => setNotifAnchorEl(e.currentTarget);
	const handleNotifClose = () => setNotifAnchorEl(null);

	const handleNotifClick = async (notif) => {
		try {
			if (!notif.isRead) {
				await notificationService.markAsRead(notif._id);
				setNotifications((prev) => prev.map((n) => n._id === notif._id ? { ...n, isRead: true } : n));
				setNotifCount((c) => Math.max(0, c - 1));
			}
		} catch { /* ignore */ }
		handleNotifClose();
		if (notif.link) navigate(notif.link);
	};

	const handleMarkAllRead = async () => {
		try {
			await notificationService.markAllAsRead();
			setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
			setNotifCount(0);
		} catch { /* ignore */ }
	};

	useEffect(() => {
		settingsService.getSettings().then((res) => setSiteSettings(res.data)).catch(() => { });
	}, []);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 8);
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	// Lấy số tin nhắn chưa đọc + map theo từng admin
	const fetchUnread = useCallback(() => {
		if (!showChat) return;
		chatService.getConversations()
			.then((res) => {
				const list = res.data || [];
				const myId = (user?.id || user?._id)?.toString();
				let total = 0;
				const byAdmin = {};
				list.forEach((c) => {
					const u = c.unreadCount || 0;
					total += u;
					if (u > 0) {
						const other = c.participants?.find((p) => p._id?.toString() !== myId);
						if (other?._id) byAdmin[other._id.toString()] = u;
					}
				});
				setUnreadCount(total);
				setUnreadByAdmin(byAdmin);
			})
			.catch(() => { });
	}, [showChat, user]);

	useEffect(() => {
		fetchUnread();
	}, [fetchUnread]);

	// Lắng nghe tin nhắn mới qua socket để cập nhật badge
	useEffect(() => {
		if (!socket || !showChat) return;
		const handler = (message) => {
			const senderId = message?.sender?._id?.toString() || message?.sender?.toString();
			const myId = (user?.id || user?._id)?.toString();
			if (!senderId || senderId === myId) return;
			// Refetch từ server (authoritative). Nếu cửa sổ chat đang mở với conv này
			// thì FloatingChatWindow sẽ markAsRead → server trả về unreadCount=0 cho conv đó.
			// Delay nhỏ để đảm bảo markAsRead chạy trước fetchUnread.
			setTimeout(() => fetchUnread(), 200);
		};
		socket.on('message:new', handler);
		return () => socket.off('message:new', handler);
	}, [socket, showChat, user, fetchUnread]);

	// Lắng nghe online/offline của admin để cập nhật danh sách real-time
	useEffect(() => {
		if (!socket || !showChat) return;
		const refetchAdmins = () => {
			userService.getActiveAdmins()
				.then((res) => setActiveAdmins(res.data || []))
				.catch(() => { });
		};
		const onOnline = () => refetchAdmins();
		const onOffline = ({ userId }) => {
			// Xóa khỏi danh sách ngay khi nhận sự kiện offline
			setActiveAdmins((prev) => prev.filter((a) => a._id?.toString() !== userId?.toString()));
		};
		socket.on('user:online', onOnline);
		socket.on('user:offline', onOffline);
		return () => {
			socket.off('user:online', onOnline);
			socket.off('user:offline', onOffline);
		};
	}, [socket, showChat]);

	const handleChatOpen = (event) => {
		setChatAnchorEl(event.currentTarget);
		setLoadingAdmins(true);
		userService.getActiveAdmins()
			.then((res) => setActiveAdmins(res.data || []))
			.catch(() => setActiveAdmins([]))
			.finally(() => setLoadingAdmins(false));
	};

	const handleChatClose = () => setChatAnchorEl(null);

	const handleStartChatWithAdmin = async (adminId) => {
		if (openingChat) return;
		setOpeningChat(true);
		try {
			const res = await chatService.getOrCreateDirect(adminId);
			const conv = res.data;
			if (conv) {
				setActiveConversation(conv);
				// Làm mới badge từ server (unread của conv này đã được đánh dấu đã đọc)
				try { await chatService.markAsRead(conv._id); } catch (e) { }
				fetchUnread();
			}
			handleChatClose();
		} catch (e) {
			// noop
		} finally {
			setOpeningChat(false);
		}
	};

	const handleCloseFloatingChat = () => {
		setActiveConversation(null);
		// Đồng bộ lại badge khi đóng cửa sổ
		fetchUnread();
	};

	const handleMenu = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleLogout = () => {
		handleClose();
		setLogoutConfirmOpen(true);
	};

	const confirmLogout = async () => {
		setLogoutConfirmOpen(false);
		await logout();
		navigate('/login');
	};

	const handleProfile = () => {
		handleClose();
		setProfileOpen(true);
	};

	return (
		<>
			<GlobalStyles styles={ `@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Inter:wght@300;400;500;600;700;800&display=swap');` } />
			<AppBar
				position="fixed"
				elevation={ 0 }
				sx={ {
					top: 0,
					left: 0,
					right: 0,
					zIndex: (theme) => theme.zIndex.appBar,
					background: (isHome && !scrolled)
						? 'linear-gradient(180deg, rgba(11,12,16,0.82) 0%, transparent 100%)'
						: 'rgba(11,12,16,0.94)',
					backdropFilter: scrolled ? 'blur(18px)' : 'none',
					WebkitBackdropFilter: scrolled ? 'blur(18px)' : 'none',
					boxShadow: 'none',
					borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
					transition: 'background 0.4s ease, backdrop-filter 0.4s ease, border-color 0.4s ease',
					color: '#fff',
				} }
			>
				<Toolbar sx={ { minHeight: { xs: 64, md: 72 }, px: { xs: 2, md: 4 } } }>
					{/* ── Hamburger (mobile only) ── */ }
					<Box
						component="button"
						onClick={ () => setMobileOpen(true) }
						aria-label="Mở menu"
						sx={ {
							display: { xs: 'flex', md: 'none' },
							flexDirection: 'column',
							justifyContent: 'space-between',
							width: 22,
							height: 12,
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							p: 0,
							mr: 2,
							flexShrink: 0,
						} }
					>
						<Box sx={ { height: '1px', bgcolor: '#fff', width: '100%' } } />
						<Box sx={ { height: '1px', bgcolor: '#fff', width: '100%' } } />
					</Box>

					{/* ── Logo ── */ }
					<Box
						component={ RouterLink }
						to="/"
						sx={ { display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 } }
					>
						{ siteSettings?.logo?.path ? (
							<Box
								component="img"
								src={ `${ LOGO_BASE }${ siteSettings.logo.path }` }
								alt={ siteSettings?.siteName || 'logo' }
								sx={ { height: { xs: 36, md: 42 }, maxWidth: 180, objectFit: 'contain' } }
							/>
						) : (
							<Typography sx={ {
								fontFamily: "'Playfair Display', Georgia, serif",
								fontSize: { xs: 16, md: 20 },
								letterSpacing: '0.2em',
								color: '#fff',
								fontWeight: 600,
								textTransform: 'uppercase',
								lineHeight: 1,
							} }>
								{ siteSettings?.siteName
									? siteSettings.siteName
									: <>Travel<Box component="span" sx={ { color: '#D4AF37', fontWeight: 400 } }>EXPEDIA</Box></>
								}
							</Typography>
						) }
					</Box>

					{/* ── Desktop nav – absolute center ── */ }
					<Box sx={ {
						position: 'absolute',
						left: '50%',
						top: '50%',
						transform: 'translate(-50%, -50%)',
						display: { xs: 'none', md: 'flex' },
						alignItems: 'center',
						gap: { md: 3.5, lg: 5 },
					} }>
						{ [
							{ label: 'Trang Chủ', to: '/' },
							...((siteSettings?.menuItems || [])
								.filter((m) => m.isActive !== false)
								.sort((a, b) => (a.order || 0) - (b.order || 0))
								.map((m) => ({ label: m.label, to: m.path || '#' }))
							),
						].map((m) => {
							const loc = location.pathname.toLowerCase();
							const target = (m.to || '').toLowerCase();
							const active = loc === target || (target !== '/' && target !== '#' && loc.startsWith(target));
							return (
								<Box
									key={ m.to + m.label }
									component={ RouterLink }
									to={ m.to }
									sx={ {
										fontSize: '10px',
										letterSpacing: '0.22em',
										textTransform: 'uppercase',
										color: active ? '#D4AF37' : 'rgba(255,255,255,0.75)',
										textDecoration: 'none',
										fontWeight: 500,
										fontFamily: "'Inter', sans-serif",
										position: 'relative',
										pb: '4px',
										transition: 'color 0.25s',
										'&::after': {
											content: '""',
											position: 'absolute',
											width: active ? '100%' : '0',
											height: '1px',
											bottom: 0,
											left: 0,
											bgcolor: '#D4AF37',
											transition: 'width 0.35s ease',
										},
										'&:hover': { color: '#D4AF37' },
										'&:hover::after': { width: '100%' },
									} }
								>
									{ m.label }
								</Box>
							);
						}) }
					</Box>

					{/* ── Spacer ── */ }
					<Box sx={ { flex: 1 } } />

					{/* ── Right actions ── */ }
					<Box sx={ { display: 'flex', alignItems: 'center', gap: 0.5 } }>
						{ isAuthenticated ? (
							<>
								{ showChat && (
									<Tooltip title="Trò chuyện với hỗ trợ viên">
										<IconButton size="large" onClick={ handleChatOpen } sx={ { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#D4AF37', bgcolor: 'transparent' } } }>
											<Badge badgeContent={ unreadCount } color="error" max={ 99 }>
												<ChatIcon />
											</Badge>
										</IconButton>
									</Tooltip>
								) }
								{ !isAdminRole && (
									<Tooltip title="Thông báo">
										<IconButton size="large" onClick={ handleNotifOpen } sx={ { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#D4AF37', bgcolor: 'transparent' } } }>
											<Badge badgeContent={ notifCount } color="error" max={ 99 }>
												{ notifCount > 0 ? <NotificationsActiveIcon /> : <NotificationsBellIcon /> }
											</Badge>
										</IconButton>
									</Tooltip>
								) }
								<Tooltip title="Tài khoản">
									<IconButton size="large" onClick={ handleMenu } sx={ { ml: 0.5, p: 0.5 } }>
										<Avatar
											src={ getAvatarUrl(user?.avatar) }
											sx={ {
												width: 34,
												height: 34,
												bgcolor: '#D4AF37',
												color: '#0B0C10',
												fontSize: 15,
												fontWeight: 700,
												border: '1px solid rgba(212,175,55,0.4)',
											} }
										>
											{ (user?.fullName || user?.name || user?.username || '?').charAt(0).toUpperCase() }
										</Avatar>
									</IconButton>
								</Tooltip>
								<Menu
									anchorEl={ anchorEl }
									open={ Boolean(anchorEl) }
									onClose={ handleClose }
									anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
									transformOrigin={ { vertical: 'top', horizontal: 'right' } }
									PaperProps={ { sx: { bgcolor: '#12131C', border: '1px solid rgba(255,255,255,0.08)', color: '#E5E5E5', borderRadius: '3px', mt: 1, minWidth: 200 } } }
								>
									<Box sx={ { px: 2, py: 1.25 } }>
										<Typography sx={ { fontWeight: 600, fontSize: 14, color: '#E5E5E5' } }>{ user?.fullName || user?.name || user?.username }</Typography>
										<Typography sx={ { fontSize: 12, color: 'rgba(255,255,255,0.4)', mt: 0.25 } }>{ user?.email }</Typography>
									</Box>
									<Divider sx={ { borderColor: 'rgba(255,255,255,0.08)' } } />
									<MenuItem onClick={ handleProfile } sx={ { color: 'rgba(255,255,255,0.8)', fontSize: 14, py: 1.25, '&:hover': { color: '#D4AF37', bgcolor: 'rgba(212,175,55,0.06)' } } }>
										<PersonIcon sx={ { mr: 1.5, fontSize: 18 } } />
										Hồ sơ
									</MenuItem>
									<MenuItem onClick={ handleLogout } sx={ { color: '#ef9a9a', fontSize: 14, py: 1.25, '&:hover': { bgcolor: 'rgba(239,154,154,0.06)' } } }>
										<LogoutIcon sx={ { mr: 1.5, fontSize: 18 } } />
										Đăng xuất
									</MenuItem>
								</Menu>
							</>
						) : (
							<Box
								component={ RouterLink }
								to="/login"
								sx={ {
									display: { xs: 'none', md: 'inline-flex' },
									alignItems: 'center',
									fontSize: '10px',
									letterSpacing: '0.22em',
									textTransform: 'uppercase',
									color: '#fff',
									textDecoration: 'none',
									fontFamily: "'Inter', sans-serif",
									fontWeight: 500,
									px: 2.5,
									py: 1,
									border: '1px solid rgba(212,175,55,0.55)',
									borderRadius: '2px',
									transition: 'all 0.25s',
									'&:hover': { borderColor: '#D4AF37', color: '#D4AF37', bgcolor: 'rgba(212,175,55,0.06)' },
								} }
							>
								Đăng Nhập
							</Box>
						) }
					</Box>
				</Toolbar>

				{/* ── Chat admin popover ── */ }
				<Popover
					open={ Boolean(chatAnchorEl) }
					anchorEl={ chatAnchorEl }
					onClose={ handleChatClose }
					anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
					transformOrigin={ { vertical: 'top', horizontal: 'right' } }
					PaperProps={ { sx: { bgcolor: '#12131C', border: '1px solid rgba(255,255,255,0.08)', color: '#E5E5E5', borderRadius: '3px', width: 340, maxHeight: 460, mt: 1 } } }
				>
					<Box sx={ { px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)' } }>
						<Typography sx={ { fontWeight: 700, color: '#E5E5E5', fontSize: 14 } }>Hỗ trợ viên đang hoạt động</Typography>
						<Typography sx={ { fontSize: 12, color: 'rgba(255,255,255,0.4)', mt: 0.25 } }>
							Chọn một quản trị viên để bắt đầu trò chuyện
						</Typography>
					</Box>

					{ loadingAdmins ? (
						<Box sx={ { display: 'flex', justifyContent: 'center', py: 4 } }>
							<CircularProgress size={ 24 } sx={ { color: '#D4AF37' } } />
						</Box>
					) : activeAdmins.length === 0 ? (
						<Box sx={ { px: 2, py: 4, textAlign: 'center' } }>
							<SupportAgentIcon sx={ { fontSize: 40, color: 'rgba(255,255,255,0.2)' } } />
							<Typography sx={ { fontSize: 13, color: 'rgba(255,255,255,0.4)', mt: 1 } }>
								Hiện chưa có hỗ trợ viên nào đang trực tuyến.
							</Typography>
						</Box>
					) : (
						<List dense disablePadding sx={ { maxHeight: 380, overflowY: 'auto' } }>
							{ activeAdmins.map((admin) => {
								const adminUnread = unreadByAdmin[admin._id?.toString()] || 0;
								return (
									<ListItem
										key={ admin._id }
										button
										disabled={ openingChat }
										onClick={ () => handleStartChatWithAdmin(admin._id) }
										sx={ {
											px: 2, py: 1.25,
											bgcolor: adminUnread > 0 ? 'rgba(212,175,55,0.06)' : 'transparent',
											'&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
										} }
									>
										<ListItemAvatar>
											<Badge
												overlap="circular"
												anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
												badgeContent={
													<Box sx={ {
														width: 10, height: 10, bgcolor: '#22c55e',
														border: '2px solid #12131C', borderRadius: '50%',
													} } />
												}
											>
												<Avatar src={ getAvatarUrl(admin.avatar) } sx={ { bgcolor: '#D4AF37', color: '#000' } }>
													{ admin.fullName?.charAt(0).toUpperCase() || 'A' }
												</Avatar>
											</Badge>
										</ListItemAvatar>
										<ListItemText
											primary={
												<Typography sx={ { fontWeight: adminUnread > 0 ? 700 : 600, color: '#E5E5E5', fontSize: 14 } }>
													{ admin.fullName || admin.username }
												</Typography>
											}
											secondary={
												adminUnread > 0 ? (
													<Typography sx={ { fontSize: 12, color: '#D4AF37', fontWeight: 600 } }>
														{ adminUnread } tin nhắn mới
													</Typography>
												) : (
													<Typography sx={ { fontSize: 12, color: '#22c55e' } }>Đang hoạt động</Typography>
												)
											}
										/>
										{ adminUnread > 0 && (
											<Box sx={ {
												ml: 1, minWidth: 22, height: 22, px: 0.75,
												bgcolor: '#D4AF37', color: '#000', borderRadius: 999,
												display: 'flex', alignItems: 'center', justifyContent: 'center',
												fontSize: 12, fontWeight: 700,
											} }>
												{ adminUnread > 99 ? '99+' : adminUnread }
											</Box>
										) }
									</ListItem>
								);
							}) }
						</List>
					) }
				</Popover>

				{/* ── Notifications popover ── */ }
				<Popover
					open={ Boolean(notifAnchorEl) }
					anchorEl={ notifAnchorEl }
					onClose={ handleNotifClose }
					anchorOrigin={ { vertical: 'bottom', horizontal: 'right' } }
					transformOrigin={ { vertical: 'top', horizontal: 'right' } }
					PaperProps={ { sx: { bgcolor: '#12131C', border: '1px solid rgba(255,255,255,0.08)', color: '#E5E5E5', borderRadius: '3px', width: 380, maxHeight: 520, mt: 1 } } }
				>
					<Box sx={ { px: 2, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' } }>
						<Box>
							<Typography sx={ { fontWeight: 700, color: '#E5E5E5', fontSize: 14 } }>Thông báo</Typography>
							{ notifCount > 0 && (
								<Typography sx={ { fontSize: 12, color: 'rgba(255,255,255,0.4)', mt: 0.25 } }>{ notifCount } chưa đọc</Typography>
							) }
						</Box>
						{ notifCount > 0 && (
							<Box component="button" onClick={ handleMarkAllRead } sx={ { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#D4AF37', fontFamily: "'Inter', sans-serif", '&:hover': { color: '#fff' }, transition: 'color 0.2s' } }>
								Đọc tất cả
							</Box>
						) }
					</Box>
					{ notifications.length === 0 ? (
						<Box sx={ { px: 2, py: 5, textAlign: 'center' } }>
							<NotificationsBellIcon sx={ { fontSize: 44, color: 'rgba(255,255,255,0.2)' } } />
							<Typography sx={ { fontSize: 13, color: 'rgba(255,255,255,0.4)', mt: 1 } }>
								Chưa có thông báo nào.
							</Typography>
						</Box>
					) : (
						<List dense disablePadding sx={ { maxHeight: 440, overflowY: 'auto' } }>
							{ notifications.map((notif) => (
								<ListItem
									key={ notif._id }
									button
									onClick={ () => handleNotifClick(notif) }
									sx={ {
										px: 2, py: 1.25, alignItems: 'flex-start',
										bgcolor: notif.isRead ? 'transparent' : 'rgba(212,175,55,0.06)',
										'&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
									} }
								>
									<ListItemText
										primary={
											<Typography sx={ { fontWeight: notif.isRead ? 500 : 700, fontSize: 14, color: '#E5E5E5' } }>
												{ notif.title }
											</Typography>
										}
										secondary={
											<>
												<Typography component="div" sx={ { fontSize: 12, color: 'rgba(255,255,255,0.45)', mt: 0.25 } }>
													{ notif.content }
												</Typography>
												<Typography component="div" sx={ { fontSize: 11, color: 'rgba(255,255,255,0.25)', mt: 0.5 } }>
													{ new Date(notif.createdAt).toLocaleString('vi-VN') }
												</Typography>
											</>
										}
									/>
									{ !notif.isRead && (
										<Box sx={ { width: 7, height: 7, borderRadius: '50%', bgcolor: '#D4AF37', mt: 1, ml: 1, flexShrink: 0 } } />
									) }
								</ListItem>
							)) }
						</List>
					) }
				</Popover>
				{/* Cửa sổ chat nổi như phía admin */ }
				{ activeConversation && (
					<FloatingChatWindow
						conversation={ activeConversation }
						onClose={ handleCloseFloatingChat }
					/>
				) }
				<ProfileModal open={ profileOpen } onClose={ () => setProfileOpen(false) } />
				<Dialog
					open={ logoutConfirmOpen }
					onClose={ () => setLogoutConfirmOpen(false) }
					maxWidth="xs"
					fullWidth
					PaperProps={ { sx: { bgcolor: '#12131C', border: '1px solid rgba(255,255,255,0.08)', color: '#E5E5E5', borderRadius: '3px' } } }
				>
					<DialogTitle sx={ { color: '#E5E5E5', fontWeight: 600 } }>Xác nhận đăng xuất</DialogTitle>
					<DialogContent>
						<DialogContentText sx={ { color: 'rgba(255,255,255,0.55)' } }>Bạn có chắc muốn đăng xuất khỏi tài khoản?</DialogContentText>
					</DialogContent>
					<DialogActions sx={ { px: 3, pb: 2, gap: 1 } }>
						<Button onClick={ () => setLogoutConfirmOpen(false) } sx={ { color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } } }>Hủy</Button>
						<Button variant="contained" onClick={ confirmLogout } sx={ { bgcolor: '#D4AF37', color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#fff' } } }>Đăng xuất</Button>
					</DialogActions>
				</Dialog>
			</AppBar>

			{/* ─────────── Mobile fullscreen overlay ─────────── */ }
			<Box sx={ {
				position: 'fixed',
				inset: 0,
				bgcolor: 'rgba(11,12,16,0.97)',
				backdropFilter: 'blur(24px)',
				WebkitBackdropFilter: 'blur(24px)',
				zIndex: (theme) => theme.zIndex.appBar + 300,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				opacity: mobileOpen ? 1 : 0,
				visibility: mobileOpen ? 'visible' : 'hidden',
				transition: 'opacity 0.4s ease, visibility 0.4s ease',
				px: 4,
				overflowY: 'auto',
			} }>
				{/* Close button */ }
				<Box
					component="button"
					onClick={ () => setMobileOpen(false) }
					sx={ {
						position: 'absolute',
						top: 20,
						right: 20,
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						color: 'rgba(255,255,255,0.5)',
						p: 0,
						display: 'flex',
						alignItems: 'center',
						transition: 'color 0.2s',
						'&:hover': { color: '#D4AF37' },
					} }
				>
					<CloseIcon sx={ { fontSize: 28 } } />
				</Box>

				{/* Brand */ }
				<Box sx={ { textAlign: 'center', mb: 5 } }>
					<Typography sx={ {
						fontFamily: "'Playfair Display', Georgia, serif",
						fontSize: 26,
						letterSpacing: '0.2em',
						color: '#fff',
						fontWeight: 600,
						textTransform: 'uppercase',
					} }>
						Travel<Box component="span" sx={ { color: '#D4AF37', fontWeight: 400 } }>EXPEDIA</Box>
					</Typography>
					<Typography sx={ { fontSize: '9px', letterSpacing: '0.38em', color: '#D4AF37', fontWeight: 600, mt: 0.5, textTransform: 'uppercase' } }>
						Hội Viên Thượng Lưu
					</Typography>
				</Box>

				{/* Nav links */ }
				<Box component="nav" sx={ { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3.5, width: '100%' } }>
					{ [
						{ label: 'Trang Chủ', to: '/' },
						...((siteSettings?.menuItems || [])
							.filter((m) => m.isActive !== false)
							.sort((a, b) => (a.order || 0) - (b.order || 0))
							.map((m) => ({ label: m.label, to: m.path || '#' }))
						),
					].map((m) => {
						const loc = location.pathname.toLowerCase();
						const target = (m.to || '').toLowerCase();
						const active = loc === target || (target !== '/' && target !== '#' && loc.startsWith(target));
						return (
							<Box
								key={ m.to + m.label }
								component={ RouterLink }
								to={ m.to }
								onClick={ () => setMobileOpen(false) }
								sx={ {
									fontFamily: "'Playfair Display', Georgia, serif",
									fontSize: 26,
									letterSpacing: '0.28em',
									textTransform: 'uppercase',
									color: active ? '#D4AF37' : '#fff',
									textDecoration: 'none',
									fontWeight: 400,
									transition: 'color 0.2s',
									'&:hover': { color: '#D4AF37' },
								} }
							>
								{ m.label }
							</Box>
						);
					}) }
				</Box>

				{/* Auth */ }
				<Box sx={ { mt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 } }>
					{ isAuthenticated ? (
						<>
							<Avatar
								src={ getAvatarUrl(user?.avatar) }
								sx={ { width: 56, height: 56, bgcolor: '#D4AF37', color: '#000', fontSize: 22, fontWeight: 700 } }
							>
								{ (user?.fullName || user?.name || user?.username || '?').charAt(0).toUpperCase() }
							</Avatar>
							<Typography sx={ { color: '#fff', fontWeight: 600, letterSpacing: '0.06em', fontSize: 15 } }>
								{ user?.fullName || user?.name || user?.username }
							</Typography>
							<Box sx={ { display: 'flex', gap: 2 } }>
								<Box
									component="button"
									onClick={ () => { setMobileOpen(false); setProfileOpen(true); } }
									sx={ {
										background: 'none', border: '1px solid rgba(212,175,55,0.5)',
										borderRadius: '2px', cursor: 'pointer',
										fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase',
										color: '#D4AF37', fontFamily: "'Inter', sans-serif",
										px: 3, py: 1, transition: 'all 0.2s',
										'&:hover': { bgcolor: 'rgba(212,175,55,0.1)', borderColor: '#D4AF37' },
									} }
								>
									Hồ Sơ
								</Box>
								<Box
									component="button"
									onClick={ () => { setMobileOpen(false); setLogoutConfirmOpen(true); } }
									sx={ {
										background: 'none', border: '1px solid rgba(239,154,154,0.4)',
										borderRadius: '2px', cursor: 'pointer',
										fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase',
										color: '#ef9a9a', fontFamily: "'Inter', sans-serif",
										px: 3, py: 1, transition: 'all 0.2s',
										'&:hover': { bgcolor: 'rgba(239,154,154,0.08)', borderColor: '#ef9a9a' },
									} }
								>
									Đăng Xuất
								</Box>
							</Box>
						</>
					) : (
						<>
							<Box
								component={ RouterLink }
								to="/login"
								onClick={ () => setMobileOpen(false) }
								sx={ {
									fontSize: '11px',
									letterSpacing: '0.22em',
									textTransform: 'uppercase',
									color: '#fff',
									textDecoration: 'none',
									fontFamily: "'Inter', sans-serif",
									fontWeight: 500,
									px: 6,
									py: 1.25,
									border: '1px solid rgba(212,175,55,0.6)',
									borderRadius: '2px',
									transition: 'all 0.25s',
									'&:hover': { borderColor: '#D4AF37', color: '#D4AF37', bgcolor: 'rgba(212,175,55,0.06)' },
								} }
							>
								Đăng Nhập
							</Box>
							<Box
								component={ RouterLink }
								to="/register"
								onClick={ () => setMobileOpen(false) }
								sx={ {
									fontSize: '10px',
									letterSpacing: '0.15em',
									color: 'rgba(255,255,255,0.35)',
									textDecoration: 'none',
									fontFamily: "'Inter', sans-serif",
									transition: 'color 0.2s',
									'&:hover': { color: '#D4AF37' },
								} }
							>
								Chưa có tài khoản? Đăng ký
							</Box>
						</>
					) }
				</Box>
			</Box>
		</>
	);
};

export default Navbar;
